// src/services/FirebaseCartService.js
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  serverTimestamp,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase/config";

class FirebaseCartService {
  constructor() {
    this.listeners = new Map(); // Track real-time listeners
  }

  // Get cart document reference
  getCartDocRef(userId) {
    return doc(db, "userCarts", userId);
  }

  // Save cart to Firebase
  async saveCart(userId, cartData) {
    try {
      console.log("🔄 Saving cart to Firebase:", { userId, cartData });

      const cartRef = this.getCartDocRef(userId);
      const cartDocument = {
        userId,
        packages: cartData.packages || [],
        items: cartData.items || [],
        lastUpdated: serverTimestamp(),
        createdAt: serverTimestamp(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
      };

      await setDoc(cartRef, cartDocument, { merge: true });
      console.log("✅ Cart saved to Firebase successfully");
      return { success: true };
    } catch (error) {
      console.error("❌ Error saving cart to Firebase:", error);
      return { success: false, error: error.message };
    }
  }

  // Load cart from Firebase
  async loadCart(userId) {
    try {
      console.log("🔍 Loading cart from Firebase for user:", userId);

      const cartRef = this.getCartDocRef(userId);
      const cartDoc = await getDoc(cartRef);

      if (cartDoc.exists()) {
        const cartData = cartDoc.data();
        console.log("✅ Cart loaded from Firebase:", cartData);

        return {
          success: true,
          cart: {
            packages: cartData.packages || [],
            items: cartData.items || [],
          },
          lastUpdated: cartData.lastUpdated,
        };
      } else {
        console.log("📭 No cart found in Firebase, returning empty cart");
        return {
          success: true,
          cart: {
            packages: [],
            items: [],
          },
          lastUpdated: null,
        };
      }
    } catch (error) {
      console.error("❌ Error loading cart from Firebase:", error);
      return {
        success: false,
        error: error.message,
        cart: { packages: [], items: [] },
      };
    }
  }

  // Add item to cart in Firebase
  async addItemToCart(userId, item, quantity = 1) {
    try {
      console.log("➕ Adding item to Firebase cart:", {
        userId,
        itemId: item.id,
        quantity,
      });

      const cartRef = this.getCartDocRef(userId);
      const cartDoc = await getDoc(cartRef);

      let currentCart = { packages: [], items: [] };
      if (cartDoc.exists()) {
        const data = cartDoc.data();
        currentCart = {
          packages: data.packages || [],
          items: data.items || [],
        };
      }

      // Check if item already exists in cart
      const existingItemIndex = currentCart.items.findIndex(
        (i) => i.id === item.id
      );

      // Calculate max allowed quantity (minimum of stock and max order limit)
      // Only use limits if they are positive numbers
      const stockLimit = (item.stockQuantity && item.stockQuantity > 0) ? item.stockQuantity : Infinity;
      const maxOrderLimit = (item.maxOrderQuantity && item.maxOrderQuantity > 0) ? item.maxOrderQuantity : Infinity;
      const maxAllowedQuantity = Math.min(stockLimit, maxOrderLimit);

      if (existingItemIndex >= 0) {
        // Update quantity
        const currentQuantity = currentCart.items[existingItemIndex].quantity;
        const newQuantity = currentQuantity + quantity;

        // Check if already at max quantity (only if max is not Infinity)
        if (maxAllowedQuantity !== Infinity && currentQuantity >= maxAllowedQuantity) {
          return {
            success: false,
            error: `Maximum quantity (${maxAllowedQuantity}) already reached for this item`,
          };
        }

        // Check stock limits (only if stock limit is not Infinity)
        if (stockLimit !== Infinity && newQuantity > stockLimit) {
          return {
            success: false,
            error: `Only ${stockLimit} units available in stock`,
          };
        }

        // Check max order quantity limits (only if max order is not Infinity)
        if (maxOrderLimit !== Infinity && newQuantity > maxOrderLimit) {
          return {
            success: false,
            error: `Maximum ${maxOrderLimit} units allowed per order for this item`,
          };
        }

        const finalQuantity = maxAllowedQuantity !== Infinity ? Math.min(newQuantity, maxAllowedQuantity) : newQuantity;
        currentCart.items[existingItemIndex].quantity = finalQuantity;
        console.log(`📈 Updated item quantity: ${item.name} -> ${finalQuantity}`);
      } else {
        // Add new item - check max allowed quantity (only if max is not Infinity)
        if (maxAllowedQuantity !== Infinity && quantity > maxAllowedQuantity) {
          return {
            success: false,
            error: maxOrderLimit < stockLimit 
              ? `Maximum ${maxOrderLimit} units allowed per order for this item`
              : `Only ${stockLimit} units available in stock`,
          };
        }

        currentCart.items.push({ ...item, quantity });
        console.log(`🆕 Added new item: ${item.name} (${quantity})`);
      }

      // Save updated cart
      await this.saveCart(userId, currentCart);

      return {
        success: true,
        cart: currentCart,
        message: `${item.name} ${
          existingItemIndex >= 0 ? "updated in" : "added to"
        } cart`,
      };
    } catch (error) {
      console.error("❌ Error adding item to cart:", error);
      return { success: false, error: error.message };
    }
  }

  // Add package to cart in Firebase
  async addPackageToCart(userId, packageData) {
    try {
      console.log("📦 Adding package to Firebase cart:", {
        userId,
        packageId: packageData.id,
      });

      const cartRef = this.getCartDocRef(userId);
      const cartDoc = await getDoc(cartRef);

      let currentCart = { packages: [], items: [] };
      if (cartDoc.exists()) {
        const data = cartDoc.data();
        currentCart = {
          packages: data.packages || [],
          items: data.items || [],
        };
      }

      // Check if package already exists in cart
      const existingPackageIndex = currentCart.packages.findIndex(
        (p) => p.id === packageData.id
      );

      if (existingPackageIndex >= 0) {
        // Update quantity
        currentCart.packages[existingPackageIndex].quantity += 1;
        console.log(`📈 Updated package quantity: ${packageData.name}`);
      } else {
        // Add new package
        currentCart.packages.push({ ...packageData, quantity: 1 });
        console.log(`🆕 Added new package: ${packageData.name}`);
      }

      // Save updated cart
      await this.saveCart(userId, currentCart);

      return {
        success: true,
        cart: currentCart,
        message: `${packageData.name} ${
          existingPackageIndex >= 0 ? "updated in" : "added to"
        } cart`,
      };
    } catch (error) {
      console.error("❌ Error adding package to cart:", error);
      return { success: false, error: error.message };
    }
  }

  // Update item quantity in cart
  async updateItemQuantity(userId, itemId, newQuantity, type = "items") {
    try {
      console.log("🔄 Updating cart quantity:", {
        userId,
        itemId,
        newQuantity,
        type,
      });

      const cartRef = this.getCartDocRef(userId);
      const cartDoc = await getDoc(cartRef);

      if (!cartDoc.exists()) {
        return { success: false, error: "Cart not found" };
      }

      const data = cartDoc.data();
      let currentCart = {
        packages: data.packages || [],
        items: data.items || [],
      };

      if (type === "items") {
        const itemIndex = currentCart.items.findIndex((i) => i.id === itemId);
        if (itemIndex >= 0) {
          if (newQuantity <= 0) {
            currentCart.items.splice(itemIndex, 1);
            console.log(`🗑️ Removed item from cart: ${itemId}`);
          } else {
            // Check minimum order quantity
            const item = currentCart.items[itemIndex];
            const minQuantity = item.minOrderQuantity || 1;

            if (newQuantity < minQuantity) {
              return {
                success: false,
                error: `Minimum order quantity is ${minQuantity}`,
              };
            }

            // Check stock limits
            if (newQuantity > item.stockQuantity) {
              return {
                success: false,
                error: `Only ${item.stockQuantity} units available`,
              };
            }

            currentCart.items[itemIndex].quantity = newQuantity;
            console.log(
              `📊 Updated item quantity: ${itemId} -> ${newQuantity}`
            );
          }
        }
      } else if (type === "packages") {
        const packageIndex = currentCart.packages.findIndex(
          (p) => p.id === itemId
        );
        if (packageIndex >= 0) {
          if (newQuantity <= 0) {
            currentCart.packages.splice(packageIndex, 1);
            console.log(`🗑️ Removed package from cart: ${itemId}`);
          } else {
            currentCart.packages[packageIndex].quantity = newQuantity;
            console.log(
              `📊 Updated package quantity: ${itemId} -> ${newQuantity}`
            );
          }
        }
      }

      // Save updated cart
      await this.saveCart(userId, currentCart);

      return {
        success: true,
        cart: currentCart,
      };
    } catch (error) {
      console.error("❌ Error updating cart quantity:", error);
      return { success: false, error: error.message };
    }
  }

  // Remove item from cart
  async removeFromCart(userId, itemId, type = "items") {
    try {
      console.log("🗑️ Removing from cart:", { userId, itemId, type });

      const cartRef = this.getCartDocRef(userId);
      const cartDoc = await getDoc(cartRef);

      if (!cartDoc.exists()) {
        return { success: false, error: "Cart not found" };
      }

      const data = cartDoc.data();
      let currentCart = {
        packages: data.packages || [],
        items: data.items || [],
      };

      if (type === "items") {
        currentCart.items = currentCart.items.filter((i) => i.id !== itemId);
      } else if (type === "packages") {
        currentCart.packages = currentCart.packages.filter(
          (p) => p.id !== itemId
        );
      }

      // Save updated cart
      await this.saveCart(userId, currentCart);

      return {
        success: true,
        cart: currentCart,
      };
    } catch (error) {
      console.error("❌ Error removing from cart:", error);
      return { success: false, error: error.message };
    }
  }

  // Clear entire cart
  async clearCart(userId) {
    try {
      console.log("🧹 Clearing cart for user:", userId);

      const cartRef = this.getCartDocRef(userId);
      await deleteDoc(cartRef);

      console.log("✅ Cart cleared successfully");
      return { success: true };
    } catch (error) {
      console.error("❌ Error clearing cart:", error);
      return { success: false, error: error.message };
    }
  }

  // Set up real-time cart listener
  setupCartListener(userId, onCartUpdate) {
    try {
      console.log("👂 Setting up real-time cart listener for:", userId);

      const cartRef = this.getCartDocRef(userId);

      // Remove existing listener if any
      if (this.listeners.has(userId)) {
        this.listeners.get(userId)();
        this.listeners.delete(userId);
      }

      // Set up new listener
      const unsubscribe = onSnapshot(
        cartRef,
        (doc) => {
          if (doc.exists()) {
            const cartData = doc.data();
            const cart = {
              packages: cartData.packages || [],
              items: cartData.items || [],
            };
            console.log("🔄 Real-time cart update received:", cart);
            onCartUpdate(cart);
          } else {
            console.log("📭 Cart document deleted, setting empty cart");
            onCartUpdate({ packages: [], items: [] });
          }
        },
        (error) => {
          console.error("❌ Error in cart listener:", error);
        }
      );

      // Store listener for cleanup
      this.listeners.set(userId, unsubscribe);

      return unsubscribe;
    } catch (error) {
      console.error("❌ Error setting up cart listener:", error);
      return null;
    }
  }

  // Remove cart listener
  removeCartListener(userId) {
    if (this.listeners.has(userId)) {
      console.log("🔇 Removing cart listener for:", userId);
      this.listeners.get(userId)();
      this.listeners.delete(userId);
    }
  }

  // Get cart item count
  async getCartItemCount(userId) {
    try {
      const result = await this.loadCart(userId);
      if (result.success) {
        const packageCount = result.cart.packages.reduce(
          (sum, p) => sum + p.quantity,
          0
        );
        const itemCount = result.cart.items.reduce(
          (sum, i) => sum + i.quantity,
          0
        );
        return packageCount + itemCount;
      }
      return 0;
    } catch (error) {
      console.error("❌ Error getting cart item count:", error);
      return 0;
    }
  }

  // Get cart total amount
  async getCartTotal(userId) {
    try {
      const result = await this.loadCart(userId);
      if (result.success) {
        const packageTotal = result.cart.packages.reduce(
          (sum, pkg) => sum + pkg.price * pkg.quantity,
          0
        );
        const itemTotal = result.cart.items.reduce(
          (sum, item) => sum + item.sellingPrice * item.quantity,
          0
        );
        return packageTotal + itemTotal;
      }
      return 0;
    } catch (error) {
      console.error("❌ Error getting cart total:", error);
      return 0;
    }
  }

  // Migrate cart from localStorage to Firebase (one-time migration)
  async migrateLocalStorageCart(userId, localStorageCart) {
    try {
      console.log("🔄 Migrating localStorage cart to Firebase:", {
        userId,
        localStorageCart,
      });

      // Check if Firebase cart already exists
      const existingCart = await this.loadCart(userId);

      if (
        existingCart.success &&
        (existingCart.cart.packages.length > 0 ||
          existingCart.cart.items.length > 0)
      ) {
        console.log("⚠️ Firebase cart already exists, skipping migration");
        return existingCart;
      }

      // Migrate localStorage cart to Firebase
      if (
        localStorageCart &&
        (localStorageCart.packages?.length > 0 ||
          localStorageCart.items?.length > 0)
      ) {
        await this.saveCart(userId, localStorageCart);
        console.log("✅ Successfully migrated localStorage cart to Firebase");

        // Clear localStorage after successful migration
        try {
          localStorage.removeItem(`infinitywhale_cart_${userId}`);
          console.log("🧹 Cleared localStorage cart after migration");
        } catch (e) {
          console.log("⚠️ Could not clear localStorage:", e);
        }

        return { success: true, cart: localStorageCart };
      }

      return existingCart;
    } catch (error) {
      console.error("❌ Error migrating cart:", error);
      return { success: false, error: error.message };
    }
  }

  // Clean up all listeners (call on app unmount)
  cleanup() {
    console.log("🧹 Cleaning up all cart listeners");
    for (const [userId, unsubscribe] of this.listeners) {
      unsubscribe();
    }
    this.listeners.clear();
  }
}

// Export singleton instance
const firebaseCartService = new FirebaseCartService();
export default firebaseCartService;
